import React, { useEffect, useMemo, useState } from "react";
import { fetchActiveFuelProducts, fuelProductLabel, type FuelProductRow } from "@/lib/fuelProducts";

type TankRow = {
  id: string;
  tank_number?: string;
  tank_name?: string;
  fuel_product_id?: string;
  fuelProductId?: string;
};

type ReadingRow = {
  id?: string;
  readingDate?: string;
  tankId?: string;
  openingStock?: number;
  stockReceived?: number;
  totalStock?: number;
  closingStock?: number;
  meterSale?: number;
  testing?: number;
  actualSales?: number;
  dipSales?: number;
  variation?: number;
};

const SalesOfficer: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [inspectionDate, setInspectionDate] = useState("");
  const [fuelProducts, setFuelProducts] = useState<FuelProductRow[]>([]);
  const [tanks, setTanks] = useState<TankRow[]>([]);
  const [readings, setReadings] = useState<ReadingRow[]>([]);
  const [activeProductId, setActiveProductId] = useState("");
  const [selectedTankId, setSelectedTankId] = useState("");
  const [dip, setDip] = useState(0);
  const [stock, setStock] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchActiveFuelProducts().then((rows) => {
      setFuelProducts(rows);
      if (rows.length > 0) {
        setActiveProductId((prev) =>
          prev && rows.some((r) => r.id === prev) ? prev : rows[0].id
        );
      }
    });
    fetch("/api/tanks-list", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) setTanks(data.rows || []);
      });
  }, []);

  const tanksForProduct = useMemo(() => {
    if (!activeProductId) return tanks;
    return tanks.filter(
      (t) =>
        String(t.fuel_product_id || t.fuelProductId || "") === String(activeProductId)
    );
  }, [tanks, activeProductId]);

  useEffect(() => {
    if (tanksForProduct.length > 0) {
      setSelectedTankId((prev) =>
        prev && tanksForProduct.some((t) => t.id === prev) ? prev : tanksForProduct[0].id
      );
    } else {
      setSelectedTankId("");
    }
  }, [tanksForProduct]);

  const loadReadings = async () => {
    if (!date || !activeProductId) {
      setReadings([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date: date.slice(0, 10),
        fuelProductId: activeProductId,
      });
      const res = await fetch(`/api/tank-daily-readings?${params}`, {
        credentials: "include",
      });
      const data = await res.json();
      setReadings(data.success ? data.rows || [] : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReadings();
  }, [date, activeProductId]);

  const activeProduct = fuelProducts.find((p) => p.id === activeProductId);
  const activeLabel = activeProduct ? fuelProductLabel(activeProduct) : "";

  const filteredReadings = readings.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return String(r.tankId || "").toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!date || !selectedTankId) {
      alert("Select date and tank");
      return;
    }
    try {
      const res = await fetch("/api/tank-daily-readings", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          readingDate: date.slice(0, 10),
          tankId: selectedTankId,
          closingStock: stock,
          meterSale: dip,
          stockReceived: 0,
          testing: 0,
          notes: inspectionDate ? `Inspection: ${inspectionDate}` : undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert("Saved successfully");
        loadReadings();
      } else {
        alert(data.error || "Save failed");
      }
    } catch {
      alert("Save failed");
    }
  };

  const tankLabel = (tankId: string) => {
    const t = tanks.find((x) => String(x.id) === String(tankId));
    if (!t) return tankId;
    return t.tank_name || t.tank_number || tankId;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          <span className="text-blue-600">Dashboard</span>
          <span> &gt; </span>
          <span className="text-gray-800">Sales Officer</span>
        </h2>
        <button type="button" className="bg-green-600 text-white px-4 py-2 rounded">
          REPORT
        </button>
      </div>

      <div className="bg-indigo-600 text-white p-4 rounded-lg flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="text-black px-2 py-1 rounded"
          />
        </div>
        <div>
          <label className="block text-sm">Inspection Date</label>
          <input
            type="date"
            value={inspectionDate}
            onChange={(e) => setInspectionDate(e.target.value)}
            className="text-black px-2 py-1 rounded"
          />
        </div>
        <div>
          <label className="block text-sm">Tank</label>
          <select
            value={selectedTankId}
            onChange={(e) => setSelectedTankId(e.target.value)}
            className="text-black px-2 py-1 rounded min-w-[140px]"
            disabled={tanksForProduct.length === 0}
          >
            {tanksForProduct.length === 0 ? (
              <option value="">No tanks for {activeLabel || "product"}</option>
            ) : (
              tanksForProduct.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.tank_name || t.tank_number || t.id}
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm">Dip Value</label>
          <input
            type="number"
            value={dip}
            onChange={(e) => setDip(Number(e.target.value))}
            className="text-black px-2 py-1 rounded"
          />
        </div>
        <div>
          <label className="block text-sm">Present Stock</label>
          <input
            type="number"
            value={stock}
            onChange={(e) => setStock(Number(e.target.value))}
            className="text-black px-2 py-1 rounded"
          />
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="ml-auto bg-lime-500 text-white px-6 py-2 rounded-full"
        >
          SAVE
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {fuelProducts.length === 0 ? (
          <span className="text-sm text-gray-600">
            No fuel products — add in Fuel Products master
          </span>
        ) : (
          fuelProducts.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActiveProductId(p.id)}
              className={`px-4 py-2 rounded ${
                activeProductId === p.id
                  ? "bg-green-700 text-white"
                  : "bg-gray-300 text-gray-800"
              }`}
            >
              {fuelProductLabel(p)}
            </button>
          ))
        )}
      </div>

      <div className="mt-4 bg-white p-4 rounded shadow">
        <div className="flex justify-between mb-3 flex-wrap gap-2">
          <div>
            <label className="mr-2">Show:</label>
            <select className="border px-2 py-1">
              <option>All</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Type to filter..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border px-2 py-1"
          />
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-6">Loading…</p>
        ) : (
          <table className="w-full border text-sm overflow-x-auto">
            <thead className="bg-gray-200">
              <tr>
                {[
                  "S.No",
                  "Date",
                  "Tank",
                  "Previous stock",
                  "Receipts",
                  "Total stock",
                  "Current stock",
                  "Meter.Sales",
                  "Testing",
                  "Actual.Sales",
                  "Dip.Sales",
                  "Variation",
                ].map((head) => (
                  <th key={head} className="border p-2 whitespace-nowrap">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredReadings.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center p-4 text-gray-500">
                    No data for {activeLabel || "selected product"} on {date}
                  </td>
                </tr>
              ) : (
                filteredReadings.map((r, i) => (
                  <tr key={r.id || i}>
                    <td className="border p-2">{i + 1}</td>
                    <td className="border p-2">{String(r.readingDate || date).slice(0, 10)}</td>
                    <td className="border p-2">{tankLabel(String(r.tankId || ""))}</td>
                    <td className="border p-2">{r.openingStock ?? 0}</td>
                    <td className="border p-2">{r.stockReceived ?? 0}</td>
                    <td className="border p-2">{r.totalStock ?? 0}</td>
                    <td className="border p-2">{r.closingStock ?? 0}</td>
                    <td className="border p-2">{r.meterSale ?? 0}</td>
                    <td className="border p-2">{r.testing ?? 0}</td>
                    <td className="border p-2">{r.actualSales ?? 0}</td>
                    <td className="border p-2">{r.dipSales ?? 0}</td>
                    <td className="border p-2">{r.variation ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalesOfficer;
